import React, { memo, useCallback, useEffect, useMemo } from 'react';

import clsx from 'clsx';
import _ from 'lodash';
import PropTypes from 'prop-types';

import { checkIsSafari } from 'utils/browser';

const UP_DIRECTION = -1;
const DOWN_DIRECTION = 1;

const disableEventsStyles = {
  pointerEvents: 'none',
};

const draggableStyles = {
  position: 'absolute',
  width: '100%',
  height: '20px',
  zIndex: 9,
};

const ScrollWrapper = ({ children, scrollRef, step = 5, tick = 25, isDragging, className }) => {
  const classes = {
    disableEvents: disableEventsStyles,
    draggable: draggableStyles,
  };

  const isSafari = checkIsSafari();

  const onScroll = useMemo(
    () =>
      _.throttle(dir => {
        scrollRef.current.scrollTo({
          top: scrollRef.current.scrollTop + step * dir,
          behavior: 'smooth',
        });
      }, tick),
    [scrollRef, step, tick],
  );

  useEffect(() => {
    return () => {
      onScroll.cancel();
    };
  }, [onScroll]);

  const onAutoScrollUp = useCallback(() => {
    onScroll(UP_DIRECTION);
  }, [onScroll]);

  const onAutoScrollDown = useCallback(() => {
    onScroll(DOWN_DIRECTION);
  }, [onScroll]);

  if (!isSafari) {
    return children;
  }

  return (
    <div className={clsx(className, 'relative h-full w-full')}>
      <div
        className={clsx(classes.draggable, { [classes.disableEvents]: !isDragging }, 'top-0')}
        onDragOver={onAutoScrollUp}
      />
      {children}
      <div
        className={clsx(classes.draggable, { [classes.disableEvents]: !isDragging }, 'bottom-0')}
        onDragOver={onAutoScrollDown}
      />
    </div>
  );
};

ScrollWrapper.propTypes = {
  scrollRef: PropTypes.object.isRequired,
  isDragging: PropTypes.bool.isRequired,
  step: PropTypes.number,
  tick: PropTypes.number,
  className: PropTypes.string,
};

export default memo(ScrollWrapper);
